import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, MapPin, Users, Ticket, QrCode, UserCheck, Calendar, Heart, Car, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import VideoTagger from '@/components/VideoTagger';
import VideoPlayer from '@/components/VideoPlayer';
import BarRatings from '@/components/BarRatings';
import ReservationDialog from '@/components/ReservationDialog';
import SinglesAtBar from '@/components/SinglesAtBar';
import RideShareDialog from '@/components/RideShareDialog';
import ShareToX from '@/components/ShareToX';
import ShareToInstagram from '@/components/ShareToInstagram';
import AICaptionGenerator from '@/components/AICaptionGenerator';
import { BarEvents } from '@/components/EventCalendar';

const BarDetail = () => {
  const { barId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [bar, setBar] = useState(null);
  const [stats, setStats] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [friends, setFriends] = useState([]);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkedIn, setCheckedIn] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [videoTags, setVideoTags] = useState([]);
  const [advertisements, setAdvertisements] = useState([]);
  const [yelpDetails, setYelpDetails] = useState(null);

  useEffect(() => {
    fetchBarData();
    fetchFriends();
    fetchRecentCheckins();
  }, [barId]);

  const fetchBarData = async () => {
    try {
      const [barRes, statsRes, discountsRes, adsRes] = await Promise.all([
        axios.get(`${API}/bars/${barId}/details`),  // Use enriched details endpoint
        axios.get(`${API}/bars/${barId}/stats`),
        axios.get(`${API}/discounts/${barId}`),
        axios.get(`${API}/bars/${barId}/advertisements`)
      ]);
      setBar(barRes.data);
      setYelpDetails(barRes.data);  // Store Yelp details separately
      setStats(statsRes.data);
      setDiscounts(discountsRes.data);
      setAdvertisements(adsRes.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load bar details');
    } finally {
      setLoading(false);
    }
  };

  const fetchFriends = async () => {
    try {
      const response = await axios.get(`${API}/friends`);
      setFriends(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchRecentCheckins = async () => {
    try {
      const response = await axios.get(`${API}/bars/${barId}/checkins`);
      setRecentCheckins(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCheckIn = async () => {
    try {
      setUploadingVideo(true);
      const formData = new FormData();
      formData.append('bar_id', barId);
      formData.append('tagged_friends', JSON.stringify(selectedFriends));
      formData.append('video_tags', JSON.stringify(videoTags));
      
      if (videoFile) {
        formData.append('video', videoFile);
      }
      
      await axios.post(`${API}/checkins`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Checked in successfully!');
      setCheckedIn(true);
      setVideoFile(null);
      setVideoTags([]);
      fetchBarData();
      fetchRecentCheckins();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to check in');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleGetQR = async (discountId) => {
    try {
      const response = await axios.get(`${API}/discounts/${discountId}/qr`);
      setQrData(response.data);
      setShowQR(true);
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate QR code');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!bar) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Bar not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 mr-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            {bar.name}
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image */}
            <Card className="glass overflow-hidden border border-slate-700 fade-in">
              {/* Photo Gallery from Yelp */}
              {yelpDetails?.photos && yelpDetails.photos.length > 0 ? (
                <div className="relative">
                  <img
                    src={yelpDetails.photos[0]}
                    alt={bar.name}
                    className="w-full h-64 object-cover"
                  />
                  {yelpDetails.photos.length > 1 && (
                    <div className="absolute bottom-2 right-2 flex gap-1">
                      {yelpDetails.photos.slice(1, 4).map((photo, idx) => (
                        <img 
                          key={idx}
                          src={photo}
                          alt=""
                          className="w-16 h-16 object-cover rounded border-2 border-white/50"
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <img
                  src={bar.image_url || '/placeholder-bar.jpg'}
                  alt={bar.name}
                  className="w-full h-64 object-cover"
                />
              )}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-2">{bar.name}</h2>
                    <p className="text-slate-300 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {bar.address}
                    </p>
                    {/* Yelp Rating & Info */}
                    {yelpDetails && (
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        {yelpDetails.rating > 0 && (
                          <span className="text-yellow-400 flex items-center gap-1">
                            ⭐ {yelpDetails.rating} ({yelpDetails.review_count} reviews)
                          </span>
                        )}
                        {yelpDetails.price && (
                          <span className="text-green-400">{yelpDetails.price}</span>
                        )}
                        {yelpDetails.phone && (
                          <a href={`tel:${yelpDetails.phone}`} className="text-blue-400 hover:underline">
                            📞 {yelpDetails.phone}
                          </a>
                        )}
                        {yelpDetails.is_open_now !== null && yelpDetails.hours && (
                          <span className={yelpDetails.is_open_now ? 'text-green-400' : 'text-red-400'}>
                            {yelpDetails.is_open_now ? '● Open Now' : '● Closed'}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Categories */}
                    {yelpDetails?.categories && yelpDetails.categories.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {yelpDetails.categories.map((cat, idx) => (
                          <Badge key={idx} className="bg-purple-500/20 text-purple-400 text-xs">
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  {stats?.is_busy && (
                    <Badge className="bg-green-500/20 text-green-400 pulse" data-testid="busy-badge">
                      🔥 BUSY NOW
                    </Badge>
                  )}
                </div>
                <p className="text-slate-300">{bar.description}</p>
                
                {/* Yelp Link */}
                {yelpDetails?.yelp_url && (
                  <a 
                    href={yelpDetails.yelp_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-sm text-red-400 hover:text-red-300"
                  >
                    View on Yelp →
                  </a>
                )}
              </div>
            </Card>

            {/* Hours from Yelp */}
            {yelpDetails?.hours && yelpDetails.hours.length > 0 && (
              <Card className="glass p-6 border border-slate-700 fade-in">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  🕐 Hours
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                    const dayHours = yelpDetails.hours.filter(h => h.day === idx);
                    const to12h = (t) => {
                      if (!t || t.length < 4) return t;
                      let hr = parseInt(t.slice(0, 2));
                      const min = t.slice(2);
                      const ampm = hr >= 12 ? 'PM' : 'AM';
                      if (hr === 0) hr = 12;
                      else if (hr > 12) hr -= 12;
                      return `${hr}:${min} ${ampm}`;
                    };
                    return (
                      <div key={day} className="flex justify-between text-slate-300">
                        <span className="font-medium">{day}</span>
                        <span>
                          {dayHours.length > 0 
                            ? dayHours.map(h => `${to12h(h.start)} - ${to12h(h.end)}`).join(', ')
                            : 'Closed'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Yelp Reviews */}
            {yelpDetails?.reviews && yelpDetails.reviews.length > 0 && (
              <Card className="glass p-6 border border-slate-700 fade-in">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  💬 Recent Reviews
                </h3>
                <div className="space-y-4">
                  {yelpDetails.reviews.map((review, idx) => (
                    <div key={idx} className="border-b border-slate-700 pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        {review.user?.image_url && (
                          <img src={review.user.image_url} alt="" className="w-8 h-8 rounded-full" />
                        )}
                        <span className="text-white font-medium">{review.user?.name || 'Anonymous'}</span>
                        <span className="text-yellow-400">{'⭐'.repeat(review.rating)}</span>
                      </div>
                      <p className="text-slate-300 text-sm">{review.text}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Stats */}
            <Card className="glass p-6 border border-slate-700 fade-in">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Live Stats
              </h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">{stats?.total_checkins || 0}</div>
                  <div className="text-sm text-slate-400">Checked In</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">{stats?.girl_guy_ratio || 'N/A'}</div>
                  <div className="text-sm text-slate-400">Girl:Guy Ratio</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">{stats?.popular_age_group || 'N/A'}</div>
                  <div className="text-sm text-slate-400">Popular Age</div>
                </div>
              </div>
              {stats?.age_demographics && Object.values(stats.age_demographics).some(v => v > 0) && (
                <div className="pt-4 border-t border-slate-700">
                  <p className="text-sm text-slate-400 mb-2">Age Demographics:</p>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(stats.age_demographics).map(([range, count]) => (
                      count > 0 && (
                        <Badge key={range} className="bg-purple-500/20 text-purple-400">
                          {range}: {count}
                        </Badge>
                      )
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Drink Specials */}
            <Card className="glass p-6 border border-slate-700 fade-in">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Ticket className="w-5 h-5 text-purple-400" />
                Drink Specials
              </h3>
              <ul className="space-y-2">
                {(bar.drink_specials || []).map((special, index) => (
                  <li key={index} className="flex items-start gap-2 text-slate-300">
                    <span className="text-purple-400 mt-1">•</span>
                    <span>{special}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Ratings & Reviews */}
            <BarRatings barId={barId} />

            {/* Recent Check-in Videos */}
            {recentCheckins.length > 0 && (
              <Card className="glass p-6 border border-slate-700 fade-in">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  Live from the Bar
                </h3>
                <div className="space-y-4">
                  {recentCheckins.slice(0, 3).map(checkin => (
                    <div key={checkin.id} className="border border-slate-700 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">
                            {checkin.user?.username?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">
                            {checkin.user?.username || 'Anonymous'}
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(checkin.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      {checkin.video_url && (
                        <div>
                          <VideoPlayer
                            videoUrl={`${process.env.REACT_APP_BACKEND_URL}${checkin.video_url}`}
                            videoTags={checkin.video_tags || []}
                            className="w-full rounded-lg"
                          />
                          {checkin.video_tags && checkin.video_tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {checkin.video_tags.map((tag, idx) => (
                                <span key={idx} className="text-xs bg-purple-600/20 text-purple-300 px-2 py-1 rounded-full">
                                  @{tag.friend_username}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Check-in */}
            <Card className="glass p-6 border border-slate-700 fade-in">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-purple-400" />
                Check In
              </h3>
              
              {/* Video Upload */}
              <div className="mb-4">
                <p className="text-sm text-slate-300 mb-2">Upload a video (optional):</p>
                <input
                  type="file"
                  accept="video/mp4,video/mov,video/avi,video/webm,video/mkv"
                  onChange={(e) => {
                    setVideoFile(e.target.files[0]);
                    setVideoTags([]);
                  }}
                  className="w-full text-sm text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 file:cursor-pointer"
                  data-testid="video-upload-input"
                />
              </div>

              {/* Video Tagging Interface */}
              {videoFile && (
                <div className="mb-4">
                  <VideoTagger
                    videoFile={videoFile}
                    friends={friends}
                    onTagsChange={setVideoTags}
                  />
                </div>
              )}

              {friends.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-slate-300 mb-2">Tag friends:</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {friends.map(friend => (
                      <label key={friend.id} className="flex items-center gap-2 text-slate-300 cursor-pointer">
                        <Checkbox
                          checked={selectedFriends.includes(friend.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedFriends([...selectedFriends, friend.id]);
                            } else {
                              setSelectedFriends(selectedFriends.filter(id => id !== friend.id));
                            }
                          }}
                          data-testid={`friend-checkbox-${friend.id}`}
                        />
                        <span>{friend.username}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              <Button
                onClick={handleCheckIn}
                className="w-full bg-purple-600 hover:bg-purple-700"
                disabled={checkedIn || uploadingVideo}
                data-testid="checkin-btn"
              >
                {uploadingVideo ? 'Uploading...' : checkedIn ? 'Checked In ✓' : 'Check In Now'}
              </Button>
              
              {/* Reserve Table Button */}
              <ReservationDialog 
                barId={barId} 
                barName={bar?.name}
                trigger={
                  <Button
                    variant="outline"
                    className="w-full mt-3 border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Reserve a Table
                  </Button>
                }
              />
              
              {/* Singles at Bar Button */}
              <div className="mt-3">
                <SinglesAtBar barId={barId} barName={bar?.name} />
              </div>
              
              {/* Ride Share Button */}
              <div className="mt-3">
                <RideShareDialog 
                  barId={barId} 
                  barName={bar?.name}
                  trigger={
                    <Button
                      variant="outline"
                      className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    >
                      <Car className="w-4 h-4 mr-2" />
                      Get a Ride
                    </Button>
                  }
                />
              </div>

              {/* Social Share Buttons */}
              <div className="mt-3 flex gap-2">
                <ShareToX
                  text={`Just checked in at ${bar?.name}! 🍻`}
                  hashtags={['NightBuZz', 'NightOut', 'Cheers']}
                  buttonText="Share to X"
                  className="flex-1"
                />
                <ShareToInstagram
                  caption={`🍻 Just checked in at ${bar?.name}! Having an amazing time! #NightBuZz #NightOut #Cheers #${bar?.name?.replace(/\s+/g, '')}`}
                  buttonText="Instagram"
                  className="flex-1"
                />
              </div>
            </Card>

            {/* AI Caption Generator */}
            {bar && (
              <AICaptionGenerator 
                barName={bar.name} 
                barType="bar"
              />
            )}

            {/* Events at this Bar */}
            {bar && (
              <Card className="glass p-6 border border-slate-700 fade-in">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-400" />
                  Upcoming Events
                </h3>
                <BarEvents barId={barId} barName={bar.name} />
              </Card>
            )}

            {/* Discounts */}
            <Card className="glass p-6 border border-slate-700 fade-in">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <QrCode className="w-5 h-5 text-purple-400" />
                Available Discounts
              </h3>
              <div className="space-y-3">
                {discounts.map(discount => (
                  <div key={discount.id} className={`p-4 rounded-lg border ${discount.is_birthday ? 'bg-gradient-to-r from-yellow-900/30 to-pink-900/30 border-yellow-500/40' : 'bg-slate-800/50 border-slate-700'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-white flex items-center gap-2">
                        {discount.is_birthday && <span className="text-lg">🎂</span>}
                        {discount.title}
                      </h4>
                      <Badge className={discount.is_birthday ? 'bg-yellow-500/20 text-yellow-400' : 'bg-purple-500/20 text-purple-400'}>
                        {discount.is_birthday ? 'FREE' : `${discount.discount_percent}% OFF`}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400 mb-3">{discount.description}</p>
                    <Button
                      size="sm"
                      onClick={() => handleGetQR(discount.id)}
                      className={`w-full ${discount.is_birthday ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                      data-testid={`get-qr-btn-${discount.id}`}
                    >
                      {discount.is_birthday ? '🎂 Get Birthday QR Code' : 'Get QR Code'}
                    </Button>
                  </div>
                ))}
              </div>
            </Card>

            {/* Advertisements */}
            {advertisements.length > 0 && (
              <Card className="glass p-6 border border-slate-700 fade-in">
                <h3 className="text-xl font-semibold text-white mb-4">Special Promotions</h3>
                <div className="space-y-4">
                  {advertisements.map(ad => (
                    <div key={ad.id} className="p-4 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg border border-indigo-500/30">
                      <h4 className="font-bold text-white text-lg mb-2">{ad.title}</h4>
                      <p className="text-slate-300 text-sm mb-3">{ad.description}</p>
                      {ad.image_url && (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${ad.image_url}`}
                          alt={ad.title}
                          className="w-full rounded-lg object-cover max-h-48"
                        />
                      )}
                      {ad.video_url && (
                        <video
                          controls
                          className="w-full rounded-lg mt-3"
                        >
                          <source src={`${process.env.REACT_APP_BACKEND_URL}${ad.video_url}`} type="video/mp4" />
                        </video>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="glass border border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Your Discount QR Code</DialogTitle>
          </DialogHeader>
          {qrData && (
            <div className="text-center">
              <img src={qrData.qr_code} alt="QR Code" className="mx-auto mb-4" data-testid="qr-code-image" />
              <h4 className="text-lg font-bold text-white mb-2">{qrData.discount.title}</h4>
              <p className="text-slate-300 mb-2">{qrData.discount.description}</p>
              <Badge className="bg-purple-500/20 text-purple-400 text-lg px-4 py-2">
                {qrData.discount.discount_percent}% OFF
              </Badge>
              <p className="text-sm text-slate-400 mt-4">
                Show this QR code to the bartender to redeem your discount
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BarDetail;